import React from "react";
import ReactDOM from "react-dom/client";
// Initialize Sentry as early as possible so it can catch errors during
// the rest of the bootstrap. No-ops if REACT_APP_SENTRY_DSN is unset.
import "@/lib/sentry";
import { Sentry } from "@/lib/sentry";
import "@/index.css";
import App from "@/App";
import { registerServiceWorker } from "@/lib/pwa-register";
import { installSyncListeners } from "@/lib/sync-queue";
import { installGlobalErrorHandlers } from "@/lib/errorTelemetry";
import { redirectTeamToApp } from "@/lib/domainMode";

// Domain collapse (2026-06): team.avidahealth.org → app.avidahealth.org.
// Runs before render so a team.* visitor is sent to the unified app.
redirectTeamToApp();

// DEF-23: make EVERY fetch send the httpOnly session cookie so the backend
// can authenticate via cookie (same-origin already does; `include` also covers
// the production reverse-proxy origin). Raw fetch() calls that build their own
// Authorization header keep working; the cookie is simply attached too.
const _origFetch = window.fetch.bind(window);
window.fetch = (input, init = {}) => {
  if (init.credentials == null) init = { ...init, credentials: "include" };
  return _origFetch(input, init);
};

// Phase 3a hardening — first-party error telemetry.
// Fires on window.onerror + window.onunhandledrejection and POSTs
// the payload to /api/telemetry/client-error (under our HIPAA
// umbrella, so no PHI leaks to a 3rd-party observer). Sentry
// remains active as a secondary sink.
installGlobalErrorHandlers();

// Suppress Twilio setSinkId errors (browser audio permission quirk - not critical)
const suppressedErrors = ['setSinkId', 'user gesture', 'User gesture'];

window.addEventListener('error', (event) => {
  if (suppressedErrors.some(err => event.message?.includes(err))) {
    event.stopImmediatePropagation();
    event.preventDefault();
    return false;
  }
});

window.addEventListener('unhandledrejection', (event) => {
  if (suppressedErrors.some(err => event.reason?.message?.includes(err))) {
    event.stopImmediatePropagation();
    event.preventDefault();
    return false;
  }
});

// Also override console.error to suppress these in React's error overlay
const originalError = console.error;
console.error = (...args) => {
  if (args.some(arg => typeof arg === 'string' && suppressedErrors.some(err => arg.includes(err)))) {
    return;
  }
  if (args[0]?.message && suppressedErrors.some(err => args[0].message.includes(err))) {
    return;
  }
  originalError.apply(console, args);
};

// ── Chunk-load recovery ─────────────────────────────────────────────
// After a new deploy, tabs still running the PREVIOUS build request hashed
// JS/CSS chunk filenames that no longer exist on the server → "Loading chunk
// N failed" / ChunkLoadError and a white screen. Recover by reloading ONCE to
// pull the fresh index.html (SW serves HTML network-first) + new chunk names.
// A sessionStorage timestamp guards against reload loops.
const CHUNK_RELOAD_KEY = "avida:chunk-reload-at";
const isChunkError = (msg) =>
  typeof msg === "string" &&
  (/Loading chunk [\w-]+ failed/i.test(msg) ||
    /Loading CSS chunk [\w-]+ failed/i.test(msg) ||
    /ChunkLoadError/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg));

const recoverFromChunkError = () => {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
    if (Date.now() - last < 10000) return; // already reloaded recently — avoid a loop
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch { /* sessionStorage unavailable — still try one reload */ }
  window.location.reload();
};

window.addEventListener("error", (event) => {
  const msg = event?.message || event?.error?.message || "";
  if (isChunkError(msg)) {
    event.preventDefault?.();
    recoverFromChunkError();
  }
});
window.addEventListener("unhandledrejection", (event) => {
  const reason = event?.reason;
  const msg = (reason && (reason.message || reason.name)) || reason || "";
  if (isChunkError(String(msg))) {
    event.preventDefault?.();
    recoverFromChunkError();
  }
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    {/* Sentry ErrorBoundary wraps the whole tree. When the DSN env var
        is unset (local dev / CI), Sentry.ErrorBoundary still passes
        children through normally — it just doesn't ship to Sentry. */}
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div style={{ padding: 24, maxWidth: 640, margin: "40px auto", fontFamily: "system-ui" }}>
          <h2 style={{ color: "#003366" }}>Something went wrong</h2>
          <p style={{ color: "#475569" }}>
            We've been notified and are looking into it. Try reloading the page,
            or contact support if it keeps happening.
          </p>
          {process.env.NODE_ENV !== "production" && error?.message && (
            <pre style={{ background: "#fef2f2", padding: 12, borderRadius: 6, fontSize: 12, overflowX: "auto" }}>
              {String(error.message)}
            </pre>
          )}
          <button
            onClick={() => { resetError(); window.location.reload(); }}
            style={{ marginTop: 12, padding: "8px 16px", background: "#003366", color: "#fff",
                     border: 0, borderRadius: 6, cursor: "pointer" }}
            data-testid="sentry-fallback-reload-btn"
          >
            Reload page
          </button>
        </div>
      )}
    >
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);

// Phase B PWA — service worker for offline shell + sync queue for
// offline field-visit writes. No-ops on localhost dev.
registerServiceWorker();
installSyncListeners();
